import api from './api';

// ---------------------------------------------------------------------------
// Types — mirror of figano-backend lottery module (LotteryCampaign / Progress /
// Proof / Draw). Populated refs may arrive as plain ids; keep unions loose.
// ---------------------------------------------------------------------------

export type LotteryCampaignStatus =
  | 'draft'
  | 'pending_approval'
  | 'active'
  | 'paused'
  | 'ended'
  | 'drawing'
  | 'finished'
  | 'rejected'
  | 'cancelled';

export type LotteryCampaignScope = 'site' | 'store';

export type LotteryMilestoneEvent =
  | 'purchase'
  | 'review'
  | 'referral_visit'
  | 'referral_signup'
  | 'referral_purchase'
  | 'wishlist'
  | 'daily_checkin'
  | 'manual';

export type LotteryAggregate = 'count' | 'sum_amount';
export type LotteryEntryMode = 'all_milestones' | 'ticket_per_milestone';
export type LotteryPrizeType = 'wallet_credit' | 'discount_code' | 'product' | 'physical' | 'custom';

export interface LotteryMilestone {
  key?: string;
  title: string;
  event: LotteryMilestoneEvent;
  aggregate: LotteryAggregate;
  target: number;
  countUnit?: 'order' | 'item';
  filters?: { storeId?: string; minAmount?: number; productIds?: string[] };
  repeatable: boolean;
  maxRepeats?: number;
  manualInstructions?: string;
}

export interface LotteryPrize {
  title: string;
  type: LotteryPrizeType;
  amount?: number;
  productId?: string;
  description?: string;
}

export interface PopulatedUser {
  _id: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
}

export interface LotteryCampaignItem {
  _id: string;
  title: string;
  slug: string;
  description?: string;
  imageUrl?: string;
  scope: LotteryCampaignScope;
  storeId?: { _id: string; name?: string } | string | null;
  status: LotteryCampaignStatus;
  startAt: string;
  endAt: string;
  entryMode: LotteryEntryMode;
  milestones: LotteryMilestone[];
  prizes: LotteryPrize[];
  eligibility?: { requirePhoneVerified?: boolean; minAccountAgeDays?: number };
  stats?: { participants?: number; ticketSeq?: number };
  createdBy?: PopulatedUser | string | null;
  rejectionReason?: string;
  createdAt?: string;
}

export interface LotteryWinner {
  rank: number;
  prizeIndex: number;
  prizeTitle: string;
  userId: PopulatedUser | string;
  ticketSeq: number;
  fulfilled: boolean;
  fulfilledAt?: string;
  fulfillNote?: string;
}

export interface LotteryDrawDoc {
  _id?: string;
  seedHash: string;
  /** فقط بعد از reveal برگردانده می‌شود */
  seed?: string;
  algorithm?: string;
  ticketCount?: number;
  winners: LotteryWinner[];
  committedAt?: string;
  revealedAt?: string;
}

export interface LotteryCampaignDetail {
  campaign: LotteryCampaignItem;
  draw: LotteryDrawDoc | null;
  stats: {
    participants: number;
    tickets: number;
    completed: number;
    suspects: number;
    referralVisits: number;
  };
}

export interface LotteryProgressItem {
  _id: string;
  userId: PopulatedUser | string;
  referralCode?: string;
  milestones: { key: string; value: number; completedCount: number; firstCompletedAt?: string }[];
  allCompletedAt?: string | null;
  ticketCount: number;
  fraudSuspect?: boolean;
  fraudReason?: string;
  referralStats?: { visits?: number; signups?: number; purchases?: number };
}

export interface LotteryProofItem {
  _id: string;
  userId: PopulatedUser | string;
  campaignId: { _id: string; title?: string; slug?: string; milestones?: LotteryMilestone[] } | string;
  milestoneKey: string;
  note?: string;
  imageUrl?: string;
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string;
  createdAt?: string;
}

export interface LotteryCampaignsQuery {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  scope?: string;
}

export interface CampaignInput {
  title: string;
  description?: string;
  imageUrl?: string;
  startAt: string;
  endAt: string;
  entryMode: LotteryEntryMode;
  milestones: LotteryMilestone[];
  prizes: LotteryPrize[];
  eligibility?: { requirePhoneVerified?: boolean; minAccountAgeDays?: number };
  activate?: boolean;
}

export interface PaginatedResponse<T> {
  items: T[];
  meta: {
    totalItems: number;
    itemCount: number;
    itemsPerPage: number;
    totalPages: number;
    currentPage: number;
  };
}

// ---------------------------------------------------------------------------
// Persian label maps (shared by page + detail modal)
// ---------------------------------------------------------------------------

export const CAMPAIGN_STATUS_LABELS: Record<LotteryCampaignStatus, string> = {
  draft: 'پیش‌نویس',
  pending_approval: 'منتظر تایید',
  active: 'فعال',
  paused: 'متوقف',
  ended: 'پایان‌یافته (آماده قرعه‌کشی)',
  drawing: 'در حال قرعه‌کشی',
  finished: 'انجام شد',
  rejected: 'رد شده',
  cancelled: 'لغو شده',
};

export const CAMPAIGN_STATUS_COLORS: Record<
  LotteryCampaignStatus,
  'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info'
> = {
  draft: 'default',
  pending_approval: 'warning',
  active: 'success',
  paused: 'warning',
  ended: 'info',
  drawing: 'secondary',
  finished: 'primary',
  rejected: 'error',
  cancelled: 'default',
};

export const SCOPE_LABELS: Record<LotteryCampaignScope, string> = {
  site: 'سراسری (سایت)',
  store: 'فروشگاهی',
};

export const EVENT_LABELS: Record<LotteryMilestoneEvent, string> = {
  purchase: 'خرید',
  review: 'دیدگاه تاییدشده',
  referral_visit: 'بازدید لینک دعوت',
  referral_signup: 'ثبت‌نام با لینک دعوت',
  referral_purchase: 'خرید دعوت‌شده',
  wishlist: 'افزودن به علاقه‌مندی',
  daily_checkin: 'حضور روزانه',
  manual: 'شرط دلخواه با مدرک',
};

export const AGGREGATE_LABELS: Record<LotteryAggregate, string> = {
  count: 'تعداد',
  sum_amount: 'مبلغ (تومان)',
};

export const ENTRY_MODE_LABELS: Record<LotteryEntryMode, string> = {
  all_milestones: 'تکمیل همه مایلستون‌ها = یک بلیت',
  ticket_per_milestone: 'هر مایلستون = یک بلیت',
};

export const PRIZE_TYPE_LABELS: Record<LotteryPrizeType, string> = {
  wallet_credit: 'شارژ کیف پول',
  discount_code: 'کد تخفیف',
  product: 'محصول',
  physical: 'فیزیکی',
  custom: 'دلخواه',
};

/** نمایش نام کاربر populate شده؛ اگر فقط id باشد، همان را برمی‌گرداند */
export const userDisplayName = (u: PopulatedUser | string | null | undefined): string => {
  if (!u) return '-';
  if (typeof u === 'string') return u;
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  return name || u.phone || u._id || '-';
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** api.post پاکت { status, data } را باز نمی‌کند؛ اینجا هر دو حالت را پوشش می‌دهیم */
const unwrap = <T>(res: any): T => {
  if (res && typeof res === 'object' && 'status' in res && 'data' in res) return res.data as T;
  return res as T;
};

const normalizePaginated = <T>(raw: any, params: { page?: number; limit?: number }): PaginatedResponse<T> => {
  if (raw && Array.isArray(raw.items)) {
    return {
      items: raw.items,
      meta: {
        totalItems: Number(raw.total || 0),
        itemCount: raw.items.length,
        itemsPerPage: Number(raw.limit || params.limit || 20),
        totalPages: Number(raw.pages || 1),
        currentPage: Number(raw.page || params.page || 1),
      },
    };
  }
  return {
    items: [],
    meta: {
      totalItems: 0,
      itemCount: 0,
      itemsPerPage: Number(params.limit || 20),
      totalPages: 0,
      currentPage: Number(params.page || 1),
    },
  };
};

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

const lotteryService = {
  async getCampaigns(query: LotteryCampaignsQuery = {}): Promise<PaginatedResponse<LotteryCampaignItem>> {
    const params: any = {};
    if (query.page) params.page = query.page;
    if (query.limit) params.limit = query.limit;
    if (query.search) params.search = query.search;
    if (query.status) params.status = query.status;
    if (query.scope) params.scope = query.scope;
    const raw: any = await api.get<any>('/admin/lottery/campaigns', { params } as any);
    return normalizePaginated<LotteryCampaignItem>(raw, params);
  },

  async getCampaignDetail(id: string): Promise<LotteryCampaignDetail> {
    const raw: any = await api.get<any>(`/admin/lottery/campaigns/${id}`);
    const data = raw?.campaign ? raw : raw?.data;
    if (!data?.campaign) throw new Error('پاسخ نامعتبر از سرور');
    return data as LotteryCampaignDetail;
  },

  async createCampaign(input: CampaignInput): Promise<LotteryCampaignItem> {
    const res = await api.post<any>('/admin/lottery/campaigns', input);
    return unwrap<LotteryCampaignItem>(res);
  },

  async updateCampaign(id: string, input: CampaignInput): Promise<LotteryCampaignItem> {
    const res = await api.put<any>(`/admin/lottery/campaigns/${id}`, input);
    return unwrap<LotteryCampaignItem>(res);
  },

  async approve(id: string): Promise<LotteryCampaignItem> {
    const res = await api.post<any>(`/admin/lottery/campaigns/${id}/approve`);
    return unwrap<LotteryCampaignItem>(res);
  },

  async reject(id: string, reason: string): Promise<LotteryCampaignItem> {
    const res = await api.post<any>(`/admin/lottery/campaigns/${id}/reject`, { reason });
    return unwrap<LotteryCampaignItem>(res);
  },

  async pause(id: string): Promise<LotteryCampaignItem> {
    const res = await api.post<any>(`/admin/lottery/campaigns/${id}/pause`);
    return unwrap<LotteryCampaignItem>(res);
  },

  async resume(id: string): Promise<LotteryCampaignItem> {
    const res = await api.post<any>(`/admin/lottery/campaigns/${id}/resume`);
    return unwrap<LotteryCampaignItem>(res);
  },

  async cancel(id: string): Promise<LotteryCampaignItem> {
    const res = await api.post<any>(`/admin/lottery/campaigns/${id}/cancel`);
    return unwrap<LotteryCampaignItem>(res);
  },

  async getParticipants(
    campaignId: string,
    query: { suspect?: boolean; page?: number; limit?: number } = {}
  ): Promise<PaginatedResponse<LotteryProgressItem>> {
    const params: any = {};
    if (query.page) params.page = query.page;
    if (query.limit) params.limit = query.limit;
    if (query.suspect) params.suspect = 'true';
    const raw: any = await api.get<any>(`/admin/lottery/campaigns/${campaignId}/participants`, { params } as any);
    return normalizePaginated<LotteryProgressItem>(raw, params);
  },

  async flagFraud(campaignId: string, userId: string, suspect: boolean, reason?: string): Promise<void> {
    await api.post<any>(`/admin/lottery/campaigns/${campaignId}/flag-fraud`, { userId, suspect, reason });
  },

  async getProofs(
    query: { status?: string; campaignId?: string; page?: number; limit?: number } = {}
  ): Promise<PaginatedResponse<LotteryProofItem>> {
    const params: any = { status: query.status || 'pending' };
    if (query.campaignId) params.campaignId = query.campaignId;
    if (query.page) params.page = query.page;
    if (query.limit) params.limit = query.limit;
    const raw: any = await api.get<any>('/admin/lottery/proofs', { params } as any);
    return normalizePaginated<LotteryProofItem>(raw, params);
  },

  async reviewProof(proofId: string, approve: boolean, rejectionReason?: string): Promise<LotteryProofItem> {
    const res = await api.post<any>(`/admin/lottery/proofs/${proofId}/review`, { approve, rejectionReason });
    return unwrap<LotteryProofItem>(res);
  },

  async commitDraw(campaignId: string): Promise<{ seedHash: string }> {
    const res = await api.post<any>(`/admin/lottery/campaigns/${campaignId}/draw/commit`);
    return unwrap<{ seedHash: string }>(res);
  },

  async executeDraw(campaignId: string): Promise<LotteryDrawDoc> {
    const res = await api.post<any>(`/admin/lottery/campaigns/${campaignId}/draw/execute`);
    return unwrap<LotteryDrawDoc>(res);
  },

  async fulfillWinner(campaignId: string, rank: number, note?: string): Promise<LotteryDrawDoc> {
    const res = await api.post<any>(
      `/admin/lottery/campaigns/${campaignId}/draw/winners/${rank}/fulfill`,
      note ? { note } : {}
    );
    return unwrap<LotteryDrawDoc>(res);
  },
};

export default lotteryService;
